import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Plus, 
  Search, 
  MoreHorizontal, 
  Edit, 
  Copy, 
  Users, 
  X,
  Trash,
  History
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_COLORS = {
  'jeu': 'bg-[#48b4ff] text-white',
  'lecture': 'bg-purple-500 text-white',
  'tcg': 'bg-[#db3b17] text-white',
  'atelier': 'bg-green-500 text-white',
  'autre': 'bg-[#645E55] text-white'
};

const TYPE_LABELS = {
  'jeu': 'Jeu',
  'lecture': 'Lecture',
  'tcg': 'TCG',
  'atelier': 'Atelier',
  'autre': 'Autre'
};

export default function AdminEvents() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [includePast, setIncludePast] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, event: null });
  const [cancelDialog, setCancelDialog] = useState({ open: false, event: null });

  useEffect(() => {
    fetchEvents();
  }, [includePast]);

  useEffect(() => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      setFilteredEvents(events.filter(event =>
        event.name.toLowerCase().includes(query)
      ));
    } else {
      setFilteredEvents(events);
    }
  }, [events, searchQuery]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BACKEND_URL}/api/admin/events?include_past=${includePast}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) throw new Error('Erreur lors du chargement');
      
      const data = await response.json();
      setEvents(data);
      setFilteredEvents(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des événements');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (eventId) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/events/${eventId}/duplicate`,
        { method: 'POST', credentials: 'include' }
      );
      
      if (!response.ok) throw new Error('Erreur lors de la duplication');
      
      const newEvent = await response.json();
      toast.success('Événement dupliqué');
      navigate(`/admin/evenements/${newEvent.event_id}`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCancel = async (eventId) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/events/${eventId}/cancel`,
        { method: 'POST', credentials: 'include' }
      );
      
      if (!response.ok) throw new Error('Erreur lors de l\'annulation');
      
      toast.success('Événement annulé et participants notifiés');
      setCancelDialog({ open: false, event: null });
      fetchEvents();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (eventId) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/events/${eventId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      
      if (!response.ok) throw new Error('Erreur lors de la suppression');
      
      toast.success('Événement supprimé');
      setDeleteDialog({ open: false, event: null });
      fetchEvents();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <AdminLayout title="Événements">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#645E55]" />
              <Input
                type="text"
                placeholder="Rechercher un événement..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-11 border-2 border-[#E5E0D8] rounded-xl"
                data-testid="search-events"
              />
            </div>
            <Button
              variant={includePast ? 'default' : 'outline'}
              onClick={() => setIncludePast(!includePast)}
              className={`h-11 rounded-xl ${includePast ? 'bg-[#645E55]' : 'border-2 border-[#E5E0D8]'}`}
              data-testid="toggle-past"
            >
              <History className="w-4 h-4 mr-2" />
              Historique
            </Button>
          </div>
          
          <Link to="/admin/evenements/nouveau">
            <Button className="btn-primary w-full md:w-auto" data-testid="create-event-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvel événement
            </Button>
          </Link>
        </div>

        {/* Results count */}
        <p className="text-[#645E55]">
          <span className="font-bold text-[#2D2A26]">{filteredEvents.length}</span> événement{filteredEvents.length !== 1 ? 's' : ''}
        </p>

        {/* Events table */}
        <div className="bg-white rounded-2xl border-2 border-[#E5E0D8] overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-[#db3b17] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-[#645E55]">Chargement...</p>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-5xl mb-4">📅</div>
              <h3 className="font-heading font-bold text-xl text-[#2D2A26] mb-2">
                {searchQuery ? 'Aucun événement trouvé' : 'Aucun événement'}
              </h3>
              <p className="text-[#645E55] mb-6">
                {searchQuery ? 'Modifiez votre recherche' : 'Créez votre premier événement'}
              </p>
              {!searchQuery && (
                <Link to="/admin/evenements/nouveau">
                  <Button className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Créer un événement
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <Table className="admin-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Événement</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Inscrits</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEvents.map((event) => {
                  const eventDate = new Date(event.date);
                  const isPast = eventDate < new Date();
                  
                  return (
                    <TableRow key={event.event_id} data-testid={`event-row-${event.event_id}`}>
                      <TableCell>
                        <Link 
                          to={`/admin/evenements/${event.event_id}`}
                          className="font-semibold text-[#2D2A26] hover:text-[#db3b17] transition-colors"
                        >
                          {event.name}
                        </Link>
                        <p className="text-sm text-[#645E55]">{event.location}</p>
                      </TableCell>
                      <TableCell>
                        <div className="text-[#2D2A26] font-medium">
                          {format(eventDate, "d MMM yyyy", { locale: fr })}
                        </div>
                        <div className="text-sm text-[#645E55]">
                          {format(eventDate, "HH'h'mm", { locale: fr })}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={TYPE_COLORS[event.event_type] || TYPE_COLORS.autre}>
                          {TYPE_LABELS[event.event_type] || event.event_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-[#645E55]" />
                          <span className="font-medium">{event.current_participants}</span>
                          <span className="text-[#645E55]">/ {event.max_participants}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          event.is_cancelled 
                            ? 'bg-red-100 text-red-700'
                            : isPast
                              ? 'bg-gray-100 text-gray-700'
                              : event.is_full 
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-green-100 text-green-700'
                        }>
                          {event.is_cancelled ? 'Annulé' : isPast ? 'Passé' : event.is_full ? 'Complet' : 'Ouvert'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/admin/evenements/${event.event_id}`)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Modifier
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/admin/evenements/${event.event_id}/participants`)}>
                              <Users className="w-4 h-4 mr-2" />
                              Participants
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDuplicate(event.event_id)}>
                              <Copy className="w-4 h-4 mr-2" />
                              Dupliquer
                            </DropdownMenuItem>
                            {!event.is_cancelled && !isPast && (
                              <DropdownMenuItem 
                                onClick={() => setCancelDialog({ open: true, event })}
                                className="text-orange-600"
                              >
                                <X className="w-4 h-4 mr-2" />
                                Annuler
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={() => setDeleteDialog({ open: true, event })}
                              className="text-red-600"
                            >
                              <Trash className="w-4 h-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Cancel dialog */}
        <AlertDialog open={cancelDialog.open} onOpenChange={(open) => setCancelDialog({ ...cancelDialog, open })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Annuler cet événement ?</AlertDialogTitle>
              <AlertDialogDescription>
                L'événement "{cancelDialog.event?.name}" sera marqué comme annulé et tous les participants seront notifiés par email.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Non, garder</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleCancel(cancelDialog.event?.event_id)}
                className="bg-orange-500 hover:bg-orange-600"
              >
                Oui, annuler
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete dialog */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer définitivement ?</AlertDialogTitle>
              <AlertDialogDescription>
                L'événement "{deleteDialog.event?.name}" et toutes ses inscriptions seront définitivement supprimés. Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleDelete(deleteDialog.event?.event_id)}
                className="bg-red-500 hover:bg-red-600"
              >
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
